import { Box, Stack } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreateUserRequest, UserRoles } from '@/models/company';
import { Validator } from '@/utils/errorHandling';
import {
    StyledDialog,
    StyledDialogContainer
} from '@/components/consent/StyledDialog';
import { InputText } from '@/components/shared/InputText';
import MobileInput from '@/components/shared/MobileInput';
import { Alert } from '@/components/shared/Alert';
import { Dropdown } from '@/components/shared/Dropdown';
import {
    OutlinedSecondaryButtonLG,
    PrimaryButtonLG
} from '@/components/shared/Button';
import { SMBold, SMSemiBold } from '@/typography/Typography';

type AddUserDialogProps = {
    open: boolean;
    onClose: () => void;
    onSubmit: (request: CreateUserRequest, onSuccess: () => void) => void;
    validator: Validator;
    setValidator: React.Dispatch<React.SetStateAction<Validator>>;
    isSubmitting?: boolean;
};

export const AddUserDialog = (props: AddUserDialogProps) => {
    const { t } = useTranslation();
    const [firstName, setFirstName] = useState<string>('');
    const [lastName, setLastName] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [role, setRole] = useState<UserRoles>(UserRoles.Admin);

    const clearState = () => {
        setFirstName('');
        setLastName('');
        setEmail('');
        setPhoneNumber('');
        setRole(UserRoles.Admin);
    };

    const onSubmit = () => {
        props.onSubmit(
            {
                firstName: firstName,
                lastName: lastName,
                email: email,
                phoneNumber: phoneNumber,
                role: role
            },
            clearState
        );
    };

    return (
        <StyledDialog open={props.open} onClose={props.onClose}>
            <StyledDialogContainer style={{ width: '440px' }}>
                <SMSemiBold mb={2}>{t('add_user.title')}</SMSemiBold>

                <Alert
                    severity="info"
                    message={t('add_user.note')}
                    sx={{ marginBottom: 0, maxHeight: 'none' }}
                />

                <InputText
                    name="first_name"
                    label={t('first_name')}
                    value={firstName}
                    error={props.validator.hasError('firstName')}
                    helperText={props.validator.getError('firstName')}
                    onChange={(event) => {
                        setFirstName(event.currentTarget.value);
                        props.setValidator(
                            props.validator.clearError('firstName')
                        );
                    }}
                    spacing="10px 0"
                />

                <InputText
                    name="last_name"
                    label={t('last_name')}
                    value={lastName}
                    error={props.validator.hasError('lastName')}
                    helperText={props.validator.getError('lastName')}
                    onChange={(event) => {
                        setLastName(event.currentTarget.value);
                        props.setValidator(
                            props.validator.clearError('lastName')
                        );
                    }}
                    spacing="10px 0"
                />

                <InputText
                    name="email"
                    type="email"
                    label={t('email')}
                    value={email}
                    error={props.validator.hasError('email')}
                    helperText={props.validator.getError('email')}
                    onChange={(event) => {
                        setEmail(event.currentTarget.value);
                        props.setValidator(props.validator.clearError('email'));
                    }}
                    spacing="10px 0"
                />

                <MobileInput
                    name="phone_number"
                    label={t('add_user.phone_number')}
                    placeholder="+XX XXXXXX"
                    value={phoneNumber}
                    error={props.validator.hasError('phoneNumber')}
                    helperText={
                        props.validator.getError(
                            'phoneNumber',
                            t('add_user.phone_number_helper')
                        )!
                    }
                    onChange={(value) => {
                        setPhoneNumber(value);
                        props.setValidator(
                            props.validator.clearError('phoneNumber')
                        );
                    }}
                />

                <Box mt={2}>
                    <SMSemiBold mb={0.5}>{t('add_user.role')}</SMSemiBold>
                    {/* Opens upward: the picker is the last field, so a downward
                        menu would be clipped by the dialog's scroll container. */}
                    <Dropdown
                        direction="up"
                        label={t('add_user.role')}
                        value={role}
                        choices={[
                            {
                                value: UserRoles.Admin,
                                label: UserRoles.Admin
                            },
                            {
                                value: UserRoles.Manager,
                                label: UserRoles.Manager
                            }
                        ]}
                        onChange={(choice) => setRole(choice as UserRoles)}
                    />
                </Box>

                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    gap={1.5}
                    mt={3}
                >
                    <OutlinedSecondaryButtonLG
                        color="secondary"
                        style={{ height: '40px', borderRadius: '8px' }}
                        onClick={props.onClose}
                        disabled={props.isSubmitting}
                    >
                        <SMBold textTransform="none">{t('cancel')}</SMBold>
                    </OutlinedSecondaryButtonLG>
                    <PrimaryButtonLG
                        style={{ height: '40px', borderRadius: '8px' }}
                        onClick={onSubmit}
                        disabled={props.isSubmitting}
                    >
                        <SMBold textTransform="none">
                            {t('add_user.submit')}
                        </SMBold>
                    </PrimaryButtonLG>
                </Stack>
            </StyledDialogContainer>
        </StyledDialog>
    );
};
